import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import NumbersIcon from '@mui/icons-material/Numbers';
import TaskIcon from '@mui/icons-material/Task';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Box, Typography } from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Chip from '@mui/material/Chip';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';

export const TaskOverview = ({ results, setExpandedTask, expandedTask }) => {
  if (!results.task_overview?.length > 0) return null;

  const handleChange = (panel) => (event, isExpanded) => {
    setExpandedTask(isExpanded ? panel : false); // Use top-level state setter
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
        Task Overview
      </Typography>
      {results.task_overview.map((task, index) => {
        const panelId = `task-panel-${index}`;
        return (
          <Accordion
            key={panelId}
            expanded={expandedTask === panelId} // Use top-level state
            onChange={handleChange(panelId)}
            sx={{ mb: 1 }}
            variant="outlined"
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls={`${panelId}-content`}
              id={`${panelId}-header`}
              sx={{ '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 2 } }}
            >
              <TaskIcon color="action" sx={{ mr: 1 }} />
              <Typography variant="subtitle1" sx={{ flexShrink: 2, fontWeight: 500 }}>
                {task.task_label}
              </Typography>
              <Chip
                icon={<NumbersIcon />}
                label={`Prevalence: ${task.prevalence}`}
                size="small"
                variant="outlined"
                sx={{ ml: 'auto' }}
              />
            </AccordionSummary>
            <AccordionDetails sx={{ borderTop: '1px solid rgba(0, 0, 0, 0.12)' }}>
              {/* Common Friction Points */}
              {task.common_friction_points?.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="subtitle2"
                    gutterBottom
                    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                  >
                    <WarningAmberIcon fontSize="small" color="error" /> Common Friction Points
                  </Typography>
                  <List dense disablePadding sx={{ pl: 3 }}>
                    {task.common_friction_points.map((point, i) => (
                      <ListItem key={i} disableGutters sx={{ alignItems: 'flex-start' }}>
                        <ListItemIcon sx={{ minWidth: 20, mt: 0.5 }}>
                          <WarningAmberIcon sx={{ fontSize: 14, color: 'error.light' }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={point}
                          primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {/* Common Enablers */}
              {task.common_enablers?.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="subtitle2"
                    gutterBottom
                    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                  >
                    <CheckCircleOutlineIcon fontSize="small" color="success" /> Common Enablers
                  </Typography>
                  <List dense disablePadding sx={{ pl: 3 }}>
                    {task.common_enablers.map((enabler, i) => (
                      <ListItem key={i} disableGutters sx={{ alignItems: 'flex-start' }}>
                        <ListItemIcon sx={{ minWidth: 20, mt: 0.5 }}>
                          <CheckCircleOutlineIcon sx={{ fontSize: 14, color: 'success.light' }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={enabler}
                          primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {/* Representative Quotes */}
              {task.representative_quotes?.length > 0 && (
                <Box>
                  <Typography
                    variant="subtitle2"
                    gutterBottom
                    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                  >
                    <FormatQuoteIcon fontSize="small" color="action" /> Representative Quotes
                  </Typography>
                  <List dense disablePadding sx={{ pl: 3 }}>
                    {task.representative_quotes.map((quote, i) => (
                      <ListItem key={i} disableGutters>
                        <ListItemIcon sx={{ minWidth: 20 }}>
                          <FormatQuoteIcon
                            sx={{ fontSize: 16, transform: 'scaleX(-1)', color: 'text.disabled' }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={quote}
                          primaryTypographyProps={{
                            variant: 'body2',
                            fontStyle: 'italic',
                            color: 'text.secondary',
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
};
